import React, { useState, useEffect, useRef } from 'react';
import { certApi } from '../../api/certApi';

export default function BulkBatchView({ activeUser, onInspectHash }) {
  const [zipFile, setZipFile] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobState, setJobState] = useState('');
  const [jobProgress, setJobProgress] = useState(0);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkError, setBulkError] = useState(null);

  const pollIntervalRef = useRef(null);

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  const startJobPolling = (targetJobId) => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);

    pollIntervalRef.current = setInterval(async () => {
      try {
        const res = await certApi.pollBatchStatus(targetJobId);
        const { status, progress, result, error } = res.data;

        setJobState(status);
        setJobProgress(progress || 0);

        if (status === 'COMPLETED') {
          clearInterval(pollIntervalRef.current);
          setBulkLoading(false);
          setBulkResult(result);
        } else if (status === 'FAILED') {
          clearInterval(pollIntervalRef.current);
          setBulkLoading(false);
          setBulkError(error || 'Background worker encountered a fatal error processing batch.');
        }
      } catch (err) {
        clearInterval(pollIntervalRef.current);
        setBulkLoading(false);
        setBulkError(err.response?.data?.error || 'Lost connection while tracking batch status.');
      }
    }, 1500);
  };

  const handleBulkUpload = async (e) => {
    e.preventDefault();
    if (!zipFile) {
      setBulkError('Please select a .zip archive containing manifest.json and PDFs.');
      return;
    }

    const formData = new FormData();
    formData.append('zipFile', zipFile);

    setBulkLoading(true);
    setBulkError(null);
    setBulkResult(null);
    setJobId(null);
    setJobProgress(0);
    setJobState('UPLOADING');

    try {
      const res = await certApi.queueBulkBatch(formData, activeUser);
      const newJobId = res.data.jobId;
      setJobId(newJobId);
      setJobState('QUEUED');
      startJobPolling(newJobId);
    } catch (err) {
      setBulkLoading(false);
      setBulkError(err.response?.data?.error || 'Failed to submit batch to queue.');
    }
  };

  return (
    <div style={{ border: '1px solid #cfd8dc', borderRadius: '8px', padding: '1.5rem', background: '#f8fafc' }}>
      <h2 style={{ color: '#0d47a1', marginTop: 0 }}>Institutional Bulk Certificate Issuance</h2>
      <p style={{ color: '#546e7a', fontSize: '0.9rem', lineHeight: '1.5' }}>
        Upload a compressed ZIP archive containing PDF credentials and a <code>manifest.json</code> descriptor.
        Jobs are queued via Redis and committed asynchronously in 200-transaction chunks.
      </p>

      {/* Upload Dropzone */}
      <form onSubmit={handleBulkUpload} style={{ marginTop: '1.2rem' }}>
        <div style={{
          padding: '20px',
          border: '2px dashed #90a4ae',
          borderRadius: '8px',
          backgroundColor: '#eceff1',
          textAlign: 'center',
          marginBottom: '1rem'
        }}>
          <input
            type="file"
            accept=".zip"
            id="zipFileInput"
            onChange={e => {
              if (e.target.files?.[0]) {
                setZipFile(e.target.files[0]);
                setBulkResult(null);
                setBulkError(null);
                setJobId(null);
                setJobProgress(0);
              }
            }}
            style={{ display: 'none' }}
          />
          <label htmlFor="zipFileInput" style={{ cursor: 'pointer', fontWeight: 'bold', color: '#37474f' }}>
            {zipFile
              ? `📦 Selected: ${zipFile.name} (${(zipFile.size / (1024 * 1024)).toFixed(2)} MB)`
              : '📁 Click to Choose cohort batch ZIP'}
          </label>
        </div>

        <button
          type="submit"
          disabled={!zipFile || bulkLoading}
          style={{
            width: '100%',
            padding: '12px',
            backgroundColor: bulkLoading ? '#607d8b' : '#1976d2',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            fontSize: '1rem',
            fontWeight: 'bold',
            cursor: (!zipFile || bulkLoading) ? 'not-allowed' : 'pointer'
          }}
        >
          {bulkLoading ? `Processing Batch in Background (${jobProgress}%)...` : 'Dispatch Batch to Redis Queue'}
        </button>
      </form>

      {/* Progress Bar Card */}
      {bulkLoading && (
        <div style={{
          marginTop: '1.5rem',
          padding: '1.2rem',
          background: '#fff',
          border: '1px solid #bbdefb',
          borderRadius: '6px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontWeight: 'bold', color: '#0d47a1' }}>Job ID: <code>{jobId || 'Allocating...'}</code></span>
            <span style={{ fontWeight: 'bold', color: '#1565c0' }}>Status: {jobState} ({jobProgress}%)</span>
          </div>

          <div style={{ background: '#e0e0e0', borderRadius: '4px', height: '16px', overflow: 'hidden' }}>
            <div style={{
              width: `${jobProgress}%`,
              background: jobProgress < 50 ? '#0288d1' : '#2e7d32',
              height: '100%',
              transition: 'width 0.4s ease'
            }} />
          </div>

          <div style={{ fontSize: '0.8rem', color: '#607d8b', marginTop: '6px' }}>
            {jobProgress < 50
              ? 'Phase 1: PDF Stream Extraction, Normalization & RFC 8785 Hashing...'
              : 'Phase 2: Committing 200-certificate transaction chunks to Hyperledger Fabric peers...'}
          </div>
        </div>
      )}

      {bulkError && (
        <div style={{ marginTop: '1.2rem', padding: '12px', backgroundColor: '#ffebee', border: '1px solid #ef5350', borderRadius: '4px', color: '#c62828' }}>
          <strong>Error:</strong> {bulkError}
        </div>
      )}

      {/* Results Dashboard */}
      {bulkResult && (
        <div style={{ marginTop: '1.5rem' }}>
          {/* Summary Metric Strip */}
          <div style={{
            display: 'flex',
            gap: '20px',
            padding: '14px',
            background: '#e8f5e9',
            border: '1px solid #a5d6a7',
            borderRadius: '6px',
            fontSize: '0.95rem'
          }}>
            <div><strong>Committed:</strong> <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>{bulkResult.totalCommitted}</span></div>
            <div><strong>Skipped/Duplicates:</strong> <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>{bulkResult.totalSkipped || 0}</span></div>
            <div><strong>Queue Status:</strong> <span style={{ color: '#1565c0', fontWeight: 'bold' }}>COMPLETED</span></div>
          </div>

          {/* Skipped Records Alert Box */}
          {bulkResult.skippedRecords && bulkResult.skippedRecords.length > 0 && (
            <div style={{
              marginTop: '1.2rem',
              padding: '1rem',
              backgroundColor: '#fff8e1',
              border: '1px solid #ffe082',
              borderRadius: '6px'
            }}>
              <h4 style={{ margin: '0 0 0.5rem 0', color: '#b78103', fontSize: '0.95rem' }}>
                ⚠️ Skipped Records ({bulkResult.skippedRecords.length})
              </h4>
              <p style={{ fontSize: '0.85rem', color: '#6d4c41', marginTop: 0 }}>
                These records were skipped because of missing attributes, missing PDFs, or duplicate registration on the ledger.
              </p>
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ffe082', borderRadius: '4px', background: '#fff' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.82rem' }}>
                  <thead style={{ background: '#fff3e0', position: 'sticky', top: 0 }}>
                    <tr>
                      <th style={{ padding: '6px 10px', borderBottom: '1px solid #ffe082' }}>#</th>
                      <th style={{ padding: '6px 10px', borderBottom: '1px solid #ffe082' }}>Cert ID / Filename</th>
                      <th style={{ padding: '6px 10px', borderBottom: '1px solid #ffe082' }}>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkResult.skippedRecords.map((item, idx) => (
                      <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#fffde7' }}>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee' }}>{idx + 1}</td>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee', fontWeight: 'bold' }}>
                          {item.certId || item.filename || 'N/A'}
                        </td>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee', color: '#c62828' }}>
                          {item.reason}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Latency Timing Breakdown */}
          {bulkResult.timing && (
            <div style={{
              marginTop: '1.2rem',
              padding: '1.2rem',
              backgroundColor: '#ffffff',
              borderRadius: '6px',
              border: '1px solid #cfd8dc'
            }}>
              <h4 style={{ margin: '0 0 0.75rem 0', color: '#263238', fontSize: '0.95rem' }}>
                ⚡ Worker Execution & Raft Consensus Metrics
              </h4>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '12px',
                textAlign: 'center'
              }}>
                <div style={{ background: '#f1f5f9', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: '600' }}>STREAM PARSING</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#0369a1', marginTop: '4px' }}>
                    {bulkResult.timing.parsingTimeSeconds}s
                  </div>
                </div>
                <div style={{ background: '#f1f5f9', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: '600' }}>FABRIC CHUNKS</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#0369a1', marginTop: '4px' }}>
                    {bulkResult.timing.fabricCommitTimeSeconds}s
                  </div>
                </div>
                <div style={{ background: '#ecfdf5', padding: '10px', borderRadius: '6px', border: '1px solid #a7f3d0' }}>
                  <div style={{ fontSize: '0.78rem', color: '#047857', fontWeight: '600' }}>TOTAL PIPELINE DURATION</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#065f46', marginTop: '4px' }}>
                    {bulkResult.timing.totalTimeSeconds}s
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Valid HTML Table of Committed Records */}
          <h4 style={{ marginTop: '1.5rem', marginBottom: '0.5rem', color: '#1a237e' }}>
            Ledger State Commits ({bulkResult.issuedRecords?.length || 0} Records)
          </h4>
          <p style={{ fontSize: '0.85rem', color: '#666', marginTop: 0 }}>
            Click <strong>"Verify Record"</strong> to inspect on the Fabric ledger.
          </p>

          <div style={{ maxHeight: '350px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem' }}>
              <thead style={{ background: '#eceff1', position: 'sticky', top: 0 }}>
                <tr>
                  <th style={{ padding: '8px 12px', borderBottom: '2px solid #cfd8dc' }}>#</th>
                  <th style={{ padding: '8px 12px', borderBottom: '2px solid #cfd8dc' }}>Certificate ID</th>
                  <th style={{ padding: '8px 12px', borderBottom: '2px solid #cfd8dc' }}>Deterministic SHA-256 Hash</th>
                  <th style={{ padding: '8px 12px', borderBottom: '2px solid #cfd8dc' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {bulkResult.issuedRecords?.map((rec, idx) => (
                  <tr key={rec.certHash || idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee' }}>{idx + 1}</td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee', fontWeight: 'bold' }}>{rec.certId}</td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee', fontFamily: 'monospace', color: '#37474f' }}>
                      {rec.certHash.substring(0, 24)}...
                    </td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee' }}>
                      <button
                        type="button"
                        onClick={() => onInspectHash(rec.certHash)}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#28a745',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          fontWeight: 'bold'
                        }}
                      >
                        Verify Record
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}